import { v } from "convex/values";

export const widgetType = v.union(
  v.literal("stat_card"),
  v.literal("bar_chart"),
  v.literal("line_chart"),
  v.literal("pie_chart"),
  v.literal("area_chart"),
  v.literal("recent_list"),
);

export const widgetDataSource = v.union(
  v.literal("packages"),
  v.literal("shipments"),
  v.literal("box_deliveries"),
  v.literal("clients"),
  v.literal("carriers"),
  v.literal("box_types"),
  v.literal("shipping_routes"),
  v.literal("shipment_stats"),
  v.literal("clients_location"),
);

export const widgetSize = v.union(
  v.literal("sm"),
  v.literal("md"),
  v.literal("lg"),
  v.literal("xl"),
);

export const widgetTimeRange = v.union(
  v.literal("all"),
  v.literal("last_24_hours"),
  v.literal("today"),
  v.literal("this_week"),
  v.literal("this_month"),
  v.literal("last_7_days"),
  v.literal("last_30_days"),
  v.literal("last_3_months"),
  v.literal("last_6_months"),
);

export const widgetValidator = v.object({
  id: v.string(),
  type: widgetType,
  dataSource: widgetDataSource,
  metric: v.string(),
  title: v.string(),
  size: widgetSize,
  order: v.number(),
  timeRange: v.optional(widgetTimeRange),
});

export const dashboardConfigValidator = v.object({
  widgets: v.array(widgetValidator),
  columns: v.number(),
  selectedShipmentId: v.optional(v.string()),
});

export const WIDGET_SIZE_COLS: Record<string, number> = {
  sm: 1,
  md: 2,
  lg: 3,
  xl: 4,
};

export const TIME_RANGE_LABELS: Record<string, string> = {
  all: "Todo el tiempo",
  today: "Hoy",
  this_week: "Esta semana",
  this_month: "Este mes",
  last_7_days: "Ultimos 7 dias",
  last_30_days: "Ultimos 30 dias",
  last_3_months: "Ultimos 3 meses",
  last_6_months: "Ultimos 6 meses",
};

function getOffsetMsForTimeZone(timeZone: string | undefined, instant: number): number {
  if (!timeZone) return 0;
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    const parts = formatter.formatToParts(new Date(instant));
    const get = (type: string) =>
      parseInt(parts.find((p) => p.type === type)?.value ?? "0", 10);
    const asUtc = Date.UTC(
      get("year"),
      get("month") - 1,
      get("day"),
      get("hour") === 24 ? 0 : get("hour"),
      get("minute"),
      get("second"),
    );
    return asUtc - instant;
  } catch {
    return 0;
  }
}

function startOfDayInTimeZone(timeZone: string | undefined, now: Date): number {
  const offsetMs = getOffsetMsForTimeZone(timeZone, now.getTime());
  const localNow = new Date(now.getTime() + offsetMs);
  localNow.setUTCHours(0, 0, 0, 0);
  return localNow.getTime() - offsetMs;
}

function startOfWeekInTimeZone(timeZone: string | undefined, now: Date): number {
  const dayStart = startOfDayInTimeZone(timeZone, now);
  const day = new Date(dayStart).getDay();
  const diff = day === 0 ? 6 : day - 1;
  return dayStart - diff * 24 * 60 * 60 * 1000;
}

function startOfMonthInTimeZone(timeZone: string | undefined, now: Date): number {
  const offsetMs = getOffsetMsForTimeZone(timeZone, now.getTime());
  const localNow = new Date(now.getTime() + offsetMs);
  const firstLocal = new Date(
    Date.UTC(localNow.getUTCFullYear(), localNow.getUTCMonth(), 1),
  );
  return firstLocal.getTime() - offsetMs;
}

export function getTimeRangeStart(
  timeRange: string,
  timeZone?: string,
): number | null {
  if (timeRange === "all") return null;

  const now = new Date();

  switch (timeRange) {
    case "last_24_hours":
      return now.getTime() - 24 * 60 * 60 * 1000;
    case "today":
      return startOfDayInTimeZone(timeZone, now);
    case "this_week":
      return startOfWeekInTimeZone(timeZone, now);
    case "this_month":
      return startOfMonthInTimeZone(timeZone, now);
    case "last_7_days":
      return now.getTime() - 7 * 24 * 60 * 60 * 1000;
    case "last_30_days":
      return now.getTime() - 30 * 24 * 60 * 60 * 1000;
    case "last_3_months":
      return now.getTime() - 90 * 24 * 60 * 60 * 1000;
    case "last_6_months":
      return now.getTime() - 180 * 24 * 60 * 60 * 1000;
    default:
      return null;
  }
}

export const AVAILABLE_METRICS: Record<
  string,
  { key: string; label: string; types: string[] }[]
> = {
  packages: [
    { key: "total", label: "Total paquetes", types: ["stat_card"] },
    { key: "boxDelivered", label: "Cajas entregadas", types: ["stat_card"] },
    { key: "received", label: "Recibidos", types: ["stat_card"] },
    { key: "consolidated", label: "Consolidados", types: ["stat_card"] },
    { key: "inTransit", label: "En transito", types: ["stat_card"] },
    { key: "delivered", label: "Entregados", types: ["stat_card"] },
    {
      key: "byStatus",
      label: "Por estado",
      types: ["bar_chart", "pie_chart"],
    },
    {
      key: "byPaymentStatus",
      label: "Por estado de pago",
      types: ["bar_chart", "pie_chart"],
    },
    { key: "totalRevenue", label: "Ingresos totales", types: ["stat_card"] },
    { key: "totalVolume", label: "Volumen total (CF)", types: ["stat_card"] },
    { key: "totalWeight", label: "Peso total (lbs)", types: ["stat_card"] },
    {
      key: "monthlyTrend",
      label: "Tendencia mensual",
      types: ["line_chart", "area_chart", "bar_chart"],
    },
    {
      key: "recentList",
      label: "Paquetes recientes",
      types: ["recent_list"],
    },
  ],
  shipments: [
    { key: "total", label: "Total contenedores", types: ["stat_card"] },
    { key: "open", label: "Abiertos", types: ["stat_card"] },
    { key: "inTransit", label: "En transito", types: ["stat_card"] },
    { key: "delivered", label: "Entregados", types: ["stat_card"] },
    {
      key: "byStatus",
      label: "Por estado",
      types: ["bar_chart", "pie_chart"],
    },
    {
      key: "byShipmentType",
      label: "Por tipo de envio",
      types: ["bar_chart", "pie_chart"],
    },
    { key: "totalPackages", label: "Total paquetes", types: ["stat_card"] },
    { key: "totalRevenue", label: "Ingresos totales", types: ["stat_card"] },
    { key: "totalVolume", label: "Volumen total (CF)", types: ["stat_card"] },
    {
      key: "availableCapacity",
      label: "Capacidad disponible",
      types: ["stat_card"],
    },
    {
      key: "fillRate",
      label: "Tasa de llenado",
      types: ["bar_chart", "pie_chart"],
    },
    {
      key: "revenueByShipment",
      label: "Ingresos por contenedor",
      types: ["bar_chart"],
    },
    {
      key: "monthlyTrend",
      label: "Tendencia mensual",
      types: ["line_chart", "area_chart", "bar_chart"],
    },
    {
      key: "recentList",
      label: "Contenedores recientes",
      types: ["recent_list"],
    },
    {
      key: "openShipmentsList",
      label: "Contenedores abiertos",
      types: ["recent_list"],
    },
  ],
  box_deliveries: [
    { key: "total", label: "Total cajas entregadas", types: ["stat_card"] },
    { key: "pendingUse", label: "Pendientes de uso", types: ["stat_card"] },
    { key: "pickedUp", label: "Recogidas", types: ["stat_card"] },
    { key: "used", label: "Usadas", types: ["stat_card"] },
    { key: "forfeited", label: "Decomisadas", types: ["stat_card"] },
    { key: "totalDeposits", label: "Total anticipos", types: ["stat_card"] },
    {
      key: "byStatus",
      label: "Por estado",
      types: ["bar_chart", "pie_chart"],
    },
    {
      key: "byBoxType",
      label: "Por tipo de caja",
      types: ["bar_chart", "pie_chart"],
    },
    {
      key: "monthlyTrend",
      label: "Tendencia mensual",
      types: ["line_chart", "area_chart", "bar_chart"],
    },
    {
      key: "recentList",
      label: "Cajas recientes",
      types: ["recent_list"],
    },
    {
      key: "pendingUseList",
      label: "Cajas pendientes de uso",
      types: ["recent_list"],
    },
  ],
  clients: [
    { key: "total", label: "Total clientes", types: ["stat_card"] },
    {
      key: "activeVsInactive",
      label: "Activos vs Inactivos",
      types: ["pie_chart", "bar_chart"],
    },
    {
      key: "byType",
      label: "Por tipo",
      types: ["pie_chart", "bar_chart"],
    },
    {
      key: "topSenders",
      label: "Top remitentes",
      types: ["bar_chart"],
    },
    {
      key: "recentList",
      label: "Clientes recientes",
      types: ["recent_list"],
    },
  ],
  carriers: [
    { key: "total", label: "Total transportistas", types: ["stat_card"] },
    {
      key: "activeVsInactive",
      label: "Activos vs Inactivos",
      types: ["pie_chart", "bar_chart"],
    },
    {
      key: "byPackageCount",
      label: "Por cantidad de paquetes",
      types: ["bar_chart"],
    },
  ],
  box_types: [
    { key: "total", label: "Total tipos de caja", types: ["stat_card"] },
    {
      key: "byUsage",
      label: "Por uso",
      types: ["bar_chart", "pie_chart"],
    },
    {
      key: "byRevenue",
      label: "Por ingresos",
      types: ["bar_chart"],
    },
  ],
  shipping_routes: [
    { key: "total", label: "Total rutas", types: ["stat_card"] },
    {
      key: "activeVsInactive",
      label: "Activas vs Inactivas",
      types: ["pie_chart", "bar_chart"],
    },
    {
      key: "byPackageCount",
      label: "Por cantidad de paquetes",
      types: ["bar_chart"],
    },
  ],
  shipment_stats: [
    { key: "boxesDelivered", label: "Cajas Entregadas", types: ["stat_card"] },
    {
      key: "totalClients",
      label: "Clientes Recolectados",
      types: ["stat_card"],
    },
    { key: "totalBilling", label: "Total Facturacion", types: ["stat_card"] },
    { key: "pendingPayments", label: "Pagos Pendientes", types: ["stat_card"] },
    { key: "totalPaid", label: "Total Pagado", types: ["stat_card"] },
    { key: "capacity", label: "Capacidad Contenedor", types: ["stat_card"] },
    {
      key: "paymentsByMethod",
      label: "Pagos por Método",
      types: ["pie_chart", "bar_chart"],
    },
    {
      key: "packagesByStatus",
      label: "Paquetes por Estado",
      types: ["pie_chart", "bar_chart"],
    },
    {
      key: "paymentsList",
      label: "Lista de Pagos",
      types: ["recent_list"],
    },
  ],
  clients_location: [
    { key: "total", label: "Total Clientes", types: ["stat_card"] },
    {
      key: "byState",
      label: "Por Estado",
      types: ["pie_chart", "bar_chart"],
    },
    {
      key: "byCity",
      label: "Por Ciudad",
      types: ["pie_chart", "bar_chart"],
    },
  ],
};

export const DEFAULT_DASHBOARD_CONFIG = {
  columns: 4,
  widgets: [
    // Fila 1: Contenedor Seleccionado - Stats (4 x sm)
    {
      id: "default-shipment-boxes-delivered",
      type: "stat_card" as const,
      dataSource: "shipment_stats" as const,
      metric: "boxesDelivered",
      title: "Cajas Entregadas",
      size: "sm" as const,
      order: 0,
      timeRange: "all" as const,
    },
    {
      id: "default-shipment-clients",
      type: "stat_card" as const,
      dataSource: "shipment_stats" as const,
      metric: "totalClients",
      title: "Clientes Recolectados",
      size: "sm" as const,
      order: 1,
      timeRange: "all" as const,
    },
    {
      id: "default-shipment-billing",
      type: "stat_card" as const,
      dataSource: "shipment_stats" as const,
      metric: "totalBilling",
      title: "Total Facturacion",
      size: "sm" as const,
      order: 2,
      timeRange: "all" as const,
    },
    {
      id: "default-shipment-pending",
      type: "stat_card" as const,
      dataSource: "shipment_stats" as const,
      metric: "pendingPayments",
      title: "Pagos Pendientes",
      size: "sm" as const,
      order: 3,
      timeRange: "all" as const,
    },
    // Fila 2: Contenedor - Mas stats (4 x sm)
    {
      id: "default-shipment-capacity",
      type: "stat_card" as const,
      dataSource: "shipment_stats" as const,
      metric: "capacity",
      title: "Capacidad Contenedor",
      size: "sm" as const,
      order: 4,
      timeRange: "all" as const,
    },
    {
      id: "default-shipment-paid",
      type: "stat_card" as const,
      dataSource: "shipment_stats" as const,
      metric: "totalPaid",
      title: "Total Pagado",
      size: "sm" as const,
      order: 5,
      timeRange: "all" as const,
    },
    {
      id: "default-packages-total",
      type: "stat_card" as const,
      dataSource: "packages" as const,
      metric: "total",
      title: "Total Paquetes",
      size: "sm" as const,
      order: 6,
      timeRange: "this_month" as const,
    },
    {
      id: "default-packages-in-transit",
      type: "stat_card" as const,
      dataSource: "packages" as const,
      metric: "inTransit",
      title: "En Transito",
      size: "sm" as const,
      order: 7,
      timeRange: "this_month" as const,
    },
    // Fila 3: Metricas generales (4 x sm)
    {
      id: "default-shipments-open",
      type: "stat_card" as const,
      dataSource: "shipments" as const,
      metric: "open",
      title: "Contenedores Abiertos",
      size: "sm" as const,
      order: 8,
      timeRange: "all" as const,
    },
    {
      id: "default-packages-revenue",
      type: "stat_card" as const,
      dataSource: "packages" as const,
      metric: "totalRevenue",
      title: "Ingresos del Mes",
      size: "sm" as const,
      order: 9,
      timeRange: "this_month" as const,
    },
    {
      id: "default-clients-total",
      type: "stat_card" as const,
      dataSource: "clients" as const,
      metric: "total",
      title: "Total Clientes",
      size: "sm" as const,
      order: 10,
      timeRange: "all" as const,
    },
    {
      id: "default-shipments-capacity",
      type: "stat_card" as const,
      dataSource: "shipments" as const,
      metric: "availableCapacity",
      title: "Capacidad Disponible",
      size: "sm" as const,
      order: 11,
      timeRange: "all" as const,
    },
    // Fila 4: Mas metricas (4 x sm)
    {
      id: "default-box-deliveries-total",
      type: "stat_card" as const,
      dataSource: "box_deliveries" as const,
      metric: "total",
      title: "Cajas Entregadas Total",
      size: "sm" as const,
      order: 12,
      timeRange: "this_month" as const,
    },
    {
      id: "default-packages-delivered",
      type: "stat_card" as const,
      dataSource: "packages" as const,
      metric: "delivered",
      title: "Entregados",
      size: "sm" as const,
      order: 13,
      timeRange: "this_month" as const,
    },
    {
      id: "default-box-deliveries-pending",
      type: "stat_card" as const,
      dataSource: "box_deliveries" as const,
      metric: "pendingUse",
      title: "Cajas Pendientes Uso",
      size: "sm" as const,
      order: 14,
      timeRange: "all" as const,
    },
    {
      id: "default-box-deliveries-used",
      type: "stat_card" as const,
      dataSource: "box_deliveries" as const,
      metric: "used",
      title: "Cajas Usadas",
      size: "sm" as const,
      order: 15,
      timeRange: "this_month" as const,
    },
    // Fila 5: Graficos (2 x sm)
    {
      id: "default-shipment-payments-by-method",
      type: "pie_chart" as const,
      dataSource: "shipment_stats" as const,
      metric: "paymentsByMethod",
      title: "Pagos por Metodo",
      size: "sm" as const,
      order: 16,
      timeRange: "all" as const,
    },
    {
      id: "default-clients-by-state",
      type: "pie_chart" as const,
      dataSource: "clients_location" as const,
      metric: "byState",
      title: "Clientes por Estado",
      size: "sm" as const,
      order: 17,
      timeRange: "all" as const,
    },
    // Fila 5 continuacion: Lista de pagos (1 x md = 2 columnas)
    {
      id: "default-shipment-payments-list",
      type: "recent_list" as const,
      dataSource: "shipment_stats" as const,
      metric: "paymentsList",
      title: "Pagos Pendientes",
      size: "md" as const,
      order: 18,
      timeRange: "all" as const,
    },
    // Fila 6: Listas (2 x md)
    {
      id: "default-shipments-open-list",
      type: "recent_list" as const,
      dataSource: "shipments" as const,
      metric: "openShipmentsList",
      title: "Capacidad de Contenedores",
      size: "md" as const,
      order: 19,
      timeRange: "all" as const,
    },
    {
      id: "default-box-deliveries-pending-list",
      type: "recent_list" as const,
      dataSource: "box_deliveries" as const,
      metric: "pendingUseList",
      title: "Cajas Pendientes de Uso",
      size: "md" as const,
      order: 20,
      timeRange: "all" as const,
    },
  ],
};
